import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Loader2, Upload, Check, ScanLine, CheckCircle2, X,
  ImagePlus
} from 'lucide-react';
import MatrixScanOverlay from './MatrixScanOverlay';

// Map agent action → border color + glow for shutter ring
const ACTION_GLOW = {
  dispatch: { border: 'border-green-400/60', shadow: '0 0 25px rgba(34,197,94,0.4)' },
  ready: { border: 'border-green-400/60', shadow: '0 0 25px rgba(34,197,94,0.4)' },
  done: { border: 'border-green-400/60', shadow: '0 0 25px rgba(34,197,94,0.4)' },
  capture: { border: 'border-cyan-400/60', shadow: '0 0 25px rgba(34,211,238,0.4)' },
  scan: { border: 'border-cyan-400/60', shadow: '0 0 25px rgba(34,211,238,0.4)' },
  search: { border: 'border-blue-400/60', shadow: '0 0 25px rgba(96,165,250,0.4)' },
};
import Card, { computeAgentStatus } from './Card';

import useSound from '../hooks/useSound';
import { useLocalCamera } from '../hooks/useLocalCamera';
import { api } from '../services/api';

export default function LiveCameraView({
  onOpenHistory,
  onViewResult,
  sessionIdRef,
}) {
  const { play } = useSound();
  const videoRef = useRef(null);

  // ── Instant local camera ──
  const localCamera = useLocalCamera(true, 'environment');

  // Detect if front camera is active (mirror the preview like native camera)
  const isFrontCamera = false; // Camera switching not available without LiveKit

  // Card state
  const [showCard, setShowCard] = useState(true);
  const [cardText, setCardText] = useState("Camera ready");

  // File input ref for album upload
  const fileInputRef = useRef(null);

  // Color filter state
  const [colorFilter, setColorFilter] = useState('none');

  // Media capture state
  const [capturedMedia, setCapturedMedia] = useState([]);
  const capturedMediaRef = useRef(capturedMedia);
  useEffect(() => { capturedMediaRef.current = capturedMedia; }, [capturedMedia]);
  // Track pending upload promises so handleDone can wait for them
  const uploadPromisesRef = useRef([]);
  const timeoutsRef = useRef([]);
  // Guard against double-navigation (shared by handleDone + handleAlbumUpload)
  const doneClickedRef = useRef(false);
  const [isStackExpanded, setIsStackExpanded] = useState(false);
  const [stackBounce, setStackBounce] = useState(false);
  const stackLongPressRef = useRef(null);

  // Agent status — without LiveKit, derive from local camera state only
  const agentComputedStatus = computeAgentStatus({
    agentIdentity: null,
    greetingReceived: false,
    userSpeaking: false,
    agentGenerating: false,
    cameraActive: localCamera.isReady,
    connectionQuality: 'unknown',
  });

  // Connection icon — camera-only mode (no LiveKit connection)
  const connectionIcon = localCamera.isReady ? 'connected' : 'offline';

  // Shutter & recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recordingTimerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const isLongPressRef = useRef(false);
  const justStartedRecordingRef = useRef(false);
  const pointerCapturedRef = useRef(false); // Track if onPointerUp already triggered photo capture

  // MediaRecorder for real video capture
  const mediaRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const autoStopTimerRef = useRef(null);
  const recordedMimeRef = useRef('video/webm');

  // Capture animation
  const [showCaptureAnim, setShowCaptureAnim] = useState(false);

  // Stack status animation
  const [stackStatus, setStackStatus] = useState('IDLE');

  // Scanning effect
  const [isScanning, setIsScanning] = useState(false);
  const scanTimerRef = useRef(null);

  // Agent-driven viewfinder overlays
  const [emojiRain, setEmojiRain] = useState(null); // {emojis: [...], duration: 3000}
  const [uiBadge, setUiBadge] = useState(null);     // {text, position, color, duration}
  const overlayTimerRef = useRef(null);

  // Lightbox (fullscreen photo preview)
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // Pre-compute emoji rain positions/sizes once per rain trigger (avoids re-randomizing on re-render)
  const [emojiRainParticles, setEmojiRainParticles] = useState([]);
  useEffect(() => {
    if (emojiRain) {
      setEmojiRainParticles(Array.from({ length: 25 }, (_, i) => ({
        x: Math.random() * 90 + 5,
        scale: 0.5 + Math.random(),
        fontSize: 20 + Math.random() * 20,
        delay: Math.random() * 1.5,
        duration: 2 + Math.random() * 2,
        rotate: Math.random() * 360,
        emojiIndex: i % emojiRain.emojis.length,
      })));
    }
  }, [emojiRain]);

  // ── Listen for agent-initiated photo captures → sync to media stack ──
  useEffect(() => {
    const handler = (e) => {
      const { src, gcsUrl } = e.detail;
      setCapturedMedia(prev => [{ type: 'photo', src, gcsUrl, fromAgent: true }, ...prev].slice(0, 8));
      setStackBounce(true);
      setTimeout(() => setStackBounce(false), 400);
      play('camera.shutter');
    };
    window.addEventListener('vi-agent-photo-captured', handler);
    return () => window.removeEventListener('vi-agent-photo-captured', handler);
  }, [play]);

  // ── Attach local camera stream to video element ──
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (localCamera.isReady && localCamera.videoRef.current) {
      // Copy srcObject from the local camera's video ref
      videoEl.srcObject = localCamera.videoRef.current.srcObject;
    }
  }, [localCamera.isReady]);

  // ── Update card text when camera becomes ready ──
  useEffect(() => {
    if (localCamera.isReady) {
      setCardText("Point your camera at anything");
      setShowCard(true);
    } else if (localCamera.error) {
      setCardText("Camera unavailable");
    }
  }, [localCamera.isReady, localCamera.error]);


  // Results are now handled in LiveSessionView (user navigates there immediately on Done)

  // Agent-driven viewfinder overlays — no-op without LiveKit DataChannel

  // ── Recording timer ──
  useEffect(() => {
    if (isRecording) {
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(recordingTimerRef.current);
    }
    return () => clearInterval(recordingTimerRef.current);
  }, [isRecording]);

  // Cleanup MediaRecorder, timers, and blob URLs on unmount
  useEffect(() => {
    return () => {
      clearTimeout(autoStopTimerRef.current);
      clearTimeout(scanTimerRef.current);
      clearTimeout(overlayTimerRef.current);
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      // Revoke any blob URLs to prevent memory leaks
      capturedMediaRef.current.forEach(item => {
        if (item.src && item.src.startsWith('blob:')) {
          URL.revokeObjectURL(item.src);
        }
      });
    };
  }, []);

  // ── Upload photo to GCS and update media entry with public URL ──
  const uploadToGCS = useCallback(async (dataUrl) => {
    const promise = (async () => {
      try {
        setStackStatus('UPLOADING');
        // Live camera captures are always novel frames — bypass the content-hash
        // cache (issue #999) so we don't bloat localStorage with one-shot hashes.
        const publicUrl = await api.uploadDataUrl(dataUrl, { skipCache: true });
        // Update the media entry matching this src with the GCS URL
        setCapturedMedia(prev => prev.map(item =>
          item.src === dataUrl ? { ...item, gcsUrl: publicUrl } : item
        ));
        setStackStatus('ANALYZING');
        const t1 = setTimeout(() => {
          setStackStatus('READY');
          const t2 = setTimeout(() => setStackStatus('IDLE'), 1500);
          timeoutsRef.current.push(t2);
        }, 1200);
        timeoutsRef.current.push(t1);
        // Notify runner via vi:media:{uid} Redis channel (fire-and-forget)
        api._notifyUploadComplete(publicUrl, '', 'image');
        return publicUrl;
      } catch (err) {
        console.error('[GCS] Upload failed:', err);
        alert(`图片上传失败：${err.message || err}`);
        setStackStatus('READY');
        const t3 = setTimeout(() => setStackStatus('IDLE'), 1500);
        timeoutsRef.current.push(t3);
        return null;
      }
    })();
    uploadPromisesRef.current.push(promise);
    return promise;
  }, []);

  // ── Upload video blob to GCS ──
  const uploadVideoToGCS = useCallback(async (blob, ext, thumbnailSrc) => {
    const promise = (async () => {
      try {
        setStackStatus('UPLOADING');
        // Live camera video clips are always novel — bypass the content-hash
        // cache (issue #999).
        const publicUrl = await api.uploadBlob(blob, ext, { skipCache: true });
        setCapturedMedia(prev => prev.map(item =>
          item.src === thumbnailSrc ? { ...item, gcsUrl: publicUrl } : item
        ));
        setStackStatus('ANALYZING');
        const t1 = setTimeout(() => {
          setStackStatus('READY');
          const t2 = setTimeout(() => setStackStatus('IDLE'), 1500);
          timeoutsRef.current.push(t2);
        }, 1200);
        timeoutsRef.current.push(t1);
        // Notify runner via vi:media:{uid} Redis channel (fire-and-forget)
        api._notifyUploadComplete(publicUrl, '', 'video');
        return publicUrl;
      } catch (err) {
        console.error('[GCS] Video upload failed:', err);
        alert(`视频上传失败：${err.message || err}`);
        setStackStatus('READY');
        const t3 = setTimeout(() => setStackStatus('IDLE'), 1500);
        timeoutsRef.current.push(t3);
        return null;
      }
    })();
    uploadPromisesRef.current.push(promise);
    return promise;
  }, []);

  // ── Album upload handler: pick files from device photo library ──
  // Goes DIRECTLY to SessionPage — no intermediate camera preview step.
  const handleAlbumUpload = useCallback(async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Prevent double-navigation
    if (doneClickedRef.current) return;
    doneClickedRef.current = true;

    play('session.send');

    // Clear previous session ref
    if (sessionIdRef) sessionIdRef.current = null;

    // Build media items and start uploads in parallel
    const mediaItems = [];
    const uploads = [];

    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      const src = URL.createObjectURL(file);

      if (isVideo) {
        let ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!['mp4', 'webm'].includes(ext)) ext = 'mp4';
        const item = { type: 'video', src, blob: file };
        mediaItems.push(item);

        // Upload and update item's gcsUrl when done
        const uploadPromise = (async () => {
          try {
            // Album-upload path treated as live-camera-equivalent by the Contract
            // (#999): bypass cache to avoid bloating localStorage with one-shot
            // hashes from device photo library picks.
            const publicUrl = await api.uploadBlob(file, ext, { skipCache: true });
            item.gcsUrl = publicUrl;
            api._notifyUploadComplete(publicUrl, '', 'video');
            return publicUrl;
          } catch (err) {
            console.error('[GCS] Video upload failed:', err);
            alert(`视频上传失败：${err.message || err}`);
            return null;
          }
        })();
        uploads.push(uploadPromise);
      } else {
        // Read image as data URL, then upload
        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.readAsDataURL(file);
        });
        // Get image dimensions before creating the media item
        const { width: imgW, height: imgH } = await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = () => resolve({ width: undefined, height: undefined });
          img.src = dataUrl;
        });
        const item = { type: 'photo', src: dataUrl, width: imgW, height: imgH };
        mediaItems.push(item);

        const uploadPromise = (async () => {
          try {
            const publicUrl = await api.uploadDataUrl(dataUrl, { skipCache: true });
            item.gcsUrl = publicUrl;
            api._notifyUploadComplete(publicUrl, '', 'image');
            return publicUrl;
          } catch (err) {
            console.error('[GCS] Upload failed:', err);
            alert(`图片上传失败：${err.message || err}`);
            return null;
          }
        })();
        uploads.push(uploadPromise);
      }
    }

    // Reset input so same file can be selected again
    e.target.value = '';

    // Navigate directly to SessionPage with the uploaded media
    play('session.enter');
    onViewResult(null, mediaItems, '', {
      fromCamera: true,
      needsPromptConfirm: true,
      uploadPromises: uploads,
    });
  }, [play, onViewResult, sessionIdRef]);

  // ── Build conversation prompt ──
  const buildConversationPrompt = useCallback(() => {
    // Without LiveKit, no voice transcripts or agent suggestions are available.
    // Return a default prompt for the session.
    return { prompt: '#ask Analyze this content', summary: '' };
  }, []);

  // ── Helper: draw video frame to canvas, mirror if front camera ──
  const drawVideoToCanvas = useCallback((video, canvas, ctx) => {
    if (isFrontCamera) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    if (isFrontCamera) {
      ctx.setTransform(1, 0, 0, 1, 0, 0); // reset
    }
  }, [isFrontCamera]);

  // ── Photo capture from real video stream ──
  const capturePhotoFromVideo = useCallback(async () => {
    // Capture directly from video element to support front camera mirroring
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      const w = videoRef.current.videoWidth || 640;
      const h = videoRef.current.videoHeight || 480;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      drawVideoToCanvas(videoRef.current, canvas, ctx);
      return { dataUrl: canvas.toDataURL('image/jpeg', 0.8), width: w, height: h };
    }
    return null;
  }, [drawVideoToCanvas]);

  // ── Shutter handlers ──
  const captureVideoThumbnail = useCallback(() => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      const w = videoRef.current.videoWidth || 640;
      const h = videoRef.current.videoHeight || 480;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      drawVideoToCanvas(videoRef.current, canvas, ctx);
      return { dataUrl: canvas.toDataURL('image/jpeg', 0.6), width: w, height: h };
    }
    return null;
  }, [drawVideoToCanvas]);

  const stopVideoRecording = useCallback(() => {
    clearTimeout(autoStopTimerRef.current);
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return null;

    // Capture thumbnail from live feed before recorder stops
    const thumbResult = captureVideoThumbnail();

    return new Promise((resolve) => {
      recorder.onstop = () => {
        const mime = recordedMimeRef.current;
        const blob = new Blob(videoChunksRef.current, { type: mime });
        videoChunksRef.current = [];
        mediaRecorderRef.current = null;
        resolve({
          blob,
          src: thumbResult?.dataUrl || URL.createObjectURL(blob),
          ext: mime.includes('mp4') ? 'mp4' : 'webm',
          width: thumbResult?.width,
          height: thumbResult?.height,
        });
      };
      recorder.stop();
    });
  }, [captureVideoThumbnail]);

  const startMediaRecorder = useCallback(() => {
    try {
      // Get stream from video element
      let stream = null;
      if (videoRef.current?.srcObject) {
        stream = videoRef.current.srcObject;
      } else if (videoRef.current?.captureStream) {
        stream = videoRef.current.captureStream();
      }
      if (!stream || !window.MediaRecorder) return false;

      // Pick best mime type
      const mimeOptions = ['video/mp4', 'video/webm;codecs=vp8', 'video/webm'];
      const mime = mimeOptions.find(m => MediaRecorder.isTypeSupported(m)) || '';
      if (!mime) return false;

      recordedMimeRef.current = mime;
      videoChunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) videoChunksRef.current.push(e.data);
      };
      recorder.start(500); // collect chunks every 500ms
      mediaRecorderRef.current = recorder;

      // Auto-stop at 30 seconds
      autoStopTimerRef.current = setTimeout(async () => {
        if (mediaRecorderRef.current?.state === 'recording') {
          play('camera.recordStop');
          setIsRecording(false);
          isLongPressRef.current = false;
          const result = await stopVideoRecording();
          if (result) {
            const { blob, src, ext, width, height } = result;
            setCapturedMedia(prev => [{ type: 'video', src, blob, width, height }, ...prev].slice(0, 8));
            uploadVideoToGCS(blob, ext, src);
          }
        }
      }, 30000);

      return true;
    } catch (err) {
      console.warn('[MediaRecorder] Failed to start:', err);
      return false;
    }
  }, [stopVideoRecording, play, uploadVideoToGCS]);

  const handleShutterDown = () => {
    if (isRecording) return;
    isLongPressRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      justStartedRecordingRef.current = true;
      setIsRecording(true);
      play('camera.recordStart');
      startMediaRecorder();
    }, 500);
  };

  const handleShutterUp = () => {
    clearTimeout(longPressTimerRef.current);
    if (isRecording) return;
    if (!isLongPressRef.current) {
      pointerCapturedRef.current = true; // Mark that pointer event handled the capture

      // If we already have media, this is a "Done" tap → go to session
      if (capturedMediaRef.current.length > 0) {
        handleDone();
        return;
      }

      play('camera.shutter');
      setShowCaptureAnim(true);
      setTimeout(async () => {
        setShowCaptureAnim(false);
        const photoResult = await capturePhotoFromVideo();
        if (photoResult) {
          const photoSrc = photoResult.dataUrl;
          setCapturedMedia(prev => [{ type: 'photo', src: photoSrc, width: photoResult.width, height: photoResult.height }, ...prev].slice(0, 8));
          setStackBounce(true);
          setTimeout(() => setStackBounce(false), 400);
          // Upload eagerly so it's ready when user taps Done
          uploadToGCS(photoSrc);
        }
      }, 600);
    }
    isLongPressRef.current = false;
  };

  const handleShutterClick = () => {
    if (justStartedRecordingRef.current) {
      justStartedRecordingRef.current = false;
      return;
    }
    if (isRecording) {
      play('camera.recordStop');
      setIsRecording(false);
      isLongPressRef.current = false;

      // Stop real video recording if active
      const videoPromise = stopVideoRecording();
      if (videoPromise) {
        videoPromise.then((result) => {
          if (result) {
            const { blob, src, ext, width, height } = result;
            setCapturedMedia(prev => [{ type: 'video', src, blob, width, height }, ...prev].slice(0, 8));
            uploadVideoToGCS(blob, ext, src);
          }
        });
      } else {
        // Fallback: capture still frame if MediaRecorder wasn't running
        setShowCaptureAnim(true);
        setTimeout(async () => {
          setShowCaptureAnim(false);
          const photoResult = await capturePhotoFromVideo();
          if (photoResult) {
            setCapturedMedia(prev => [{ type: 'video', src: photoResult.dataUrl, width: photoResult.width, height: photoResult.height }, ...prev].slice(0, 8));
            uploadToGCS(photoResult.dataUrl);
          }
        }, 600);
      }
      return;
    }
    // Pointer event already handled this action
    if (pointerCapturedRef.current) {
      pointerCapturedRef.current = false;
      return;
    }
    // Has media → Done (go to session)
    if (capturedMediaRef.current.length > 0) {
      handleDone();
      return;
    }
    // No media → take a photo as fallback
    play('camera.shutter');
    setShowCaptureAnim(true);
    setTimeout(async () => {
      setShowCaptureAnim(false);
      const photoResult = await capturePhotoFromVideo();
      if (photoResult) {
        setCapturedMedia(prev => [{ type: 'photo', src: photoResult.dataUrl, width: photoResult.width, height: photoResult.height }, ...prev].slice(0, 8));
        setStackBounce(true);
        setTimeout(() => setStackBounce(false), 400);
        uploadToGCS(photoResult.dataUrl);
      }
    }, 600);
  };

  // ── Done handler: compile conversation → navigate to session (dispatch deferred to session) ──
  // Reset done guard when component mounts (returning from session view)
  useEffect(() => {
    doneClickedRef.current = false;
  }, []);
  const handleDone = async () => {
    if (doneClickedRef.current) return;
    doneClickedRef.current = true;
    play('session.send');

    // Clear previous session ref
    if (sessionIdRef) sessionIdRef.current = null;

    // Build conversation prompt and summary
    const { prompt, summary } = buildConversationPrompt();

    // Trigger uploads for any media not yet uploaded
    for (const item of capturedMediaRef.current) {
      if (!item.gcsUrl && item.src) {
        if (item.type === 'video' && item.blob) {
          uploadVideoToGCS(item.blob, 'webm', item.src);
        } else {
          uploadToGCS(item.src);
        }
      }
    }

    // Navigate to session — dispatch is deferred until user confirms prompt
    play('session.enter');
    onViewResult(null, capturedMediaRef.current.slice(), prompt, {
      fromCamera: true,
      needsPromptConfirm: true,
      conversationSummary: summary,
      uploadPromises: uploadPromisesRef.current.slice(),
    });
    uploadPromisesRef.current = [];
  };

  // ── Action card option handler ──
  const handleActionCardOption = (option) => {
    api.dispatchExec({ prompt: option }).catch(e => console.error('[action] dispatch failed:', e));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative w-full h-full flex flex-col bg-black pb-0 md:pb-4"
    >
      {/* Fullscreen Viewfinder */}
      <div className="absolute inset-0 overflow-hidden" style={{ background: '#0a0a0a' }}>
        {/* Video element always in DOM — ready for instant stream attachment.
             Shows black (from parent bg) until stream is attached. */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            ...(isFrontCamera ? { transform: 'scaleX(-1)' } : {}),
            filter: colorFilter === 'none' ? 'none' :
              colorFilter === 'grayscale' ? 'grayscale(100%)' :
              colorFilter === 'sepia' ? 'sepia(100%)' :
              colorFilter === 'warm' ? 'sepia(30%) saturate(140%) brightness(105%)' :
              colorFilter === 'cool' ? 'saturate(80%) brightness(105%) hue-rotate(10deg)' :
              colorFilter === 'high-contrast' ? 'contrast(150%) saturate(120%)' :
              colorFilter === 'vivid' ? 'saturate(180%) contrast(110%)' :
              'none'
          }}
          onLoadedData={() => {
            if (!window.__cameraTimingLogged) {
              window.__cameraTimingLogged = true;
              console.log(`[perf] First camera frame: ${Math.round(performance.now())}ms from page load`);
            }
          }}
        />

        {/* Image/Video Stack — float + bounce */}
        <AnimatePresence mode="wait">
          {capturedMedia.length > 0 && !isStackExpanded && (
            <motion.div
              key="media-stack"
              initial={{ opacity: 0, scale: 0.5, y: 20 }}
              animate={{
                opacity: 1,
                scale: stackBounce ? [1, 1.15, 0.95, 1.05, 1] : 1,
                y: 0
              }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              onClick={() => {
                if (capturedMedia.length > 1) setIsStackExpanded(true);
                else if (capturedMedia.length === 1) setLightboxPhoto(capturedMedia[0].src);
              }}
              className="absolute w-11 h-11 cursor-pointer z-30 float-drift"
              style={{ bottom: '130px', left: '1.5rem' }}
            >
              {capturedMedia.slice(0, 4).map((item, index) => (
                <div
                  key={index}
                  className="absolute top-0 left-0 w-11 h-11 rounded-xl border border-white/20 bg-black/30 backdrop-blur-md overflow-hidden shadow-lg flex items-center justify-center"
                  style={{
                    transform: `rotate(${index * 4}deg) scale(${1 - index * 0.05})`,
                    zIndex: 4 - index,
                  }}
                >
                  <img src={item.src} alt="" className="w-full h-full object-cover opacity-80 absolute inset-0" />
                  {item.type === 'video' && (
                    <div className="z-10 w-4 h-4 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-sm">
                      <div className="w-0 h-0 border-t-[3px] border-t-transparent border-l-[5px] border-l-white border-b-[3px] border-b-transparent ml-0.5" />
                    </div>
                  )}
                </div>
              ))}

              {/* Delete button — only show when single item */}
              {capturedMedia.length === 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    play('media.delete');
                    setCapturedMedia([]);
                  }}
                  className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center z-20 hover:bg-black/80 active:scale-90 transition-all"
                >
                  <span className="text-white/80 text-[10px] font-bold leading-none">✕</span>
                </button>
              )}

              {/* Count badge — white bg + black text, spring animation */}
              <motion.div
                key={capturedMedia.length}
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500 }}
                className="absolute -bottom-1 -right-1 bg-white text-black font-bold rounded-full flex items-center justify-center z-10 shadow-md border border-black/10"
                style={{ fontSize: '9px', minWidth: '18px', minHeight: '18px', width: '18px', height: '18px' }}
              >
                {capturedMedia.length}
              </motion.div>

              {/* Stack Status Indicator */}
              <AnimatePresence mode="wait">
                {stackStatus === 'UPLOADING' && (
                  <motion.div key="uploading" initial={{ opacity: 0, scale: 0.5, rotate: -90 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} exit={{ opacity: 0, scale: 0.5 }} transition={{ type: 'spring', stiffness: 400 }}
                    className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-white/70 backdrop-blur-md flex items-center justify-center z-20 shadow-md border border-white/30">
                    <Upload size={10} strokeWidth={3} className="text-black animate-bounce" />
                  </motion.div>
                )}
                {stackStatus === 'ANALYZING' && (
                  <motion.div key="analyzing" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.5 }} transition={{ type: 'spring', stiffness: 400 }}
                    className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-white/50 backdrop-blur-md flex items-center justify-center z-20 shadow-md border border-white/20">
                    <ScanLine size={10} strokeWidth={3} className="text-black animate-pulse" />
                  </motion.div>
                )}
                {stackStatus === 'READY' && (
                  <motion.div key="ready" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: [1, 1.3, 1] }} exit={{ opacity: 0, scale: 0.5 }} transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                    className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center z-20 shadow-md border border-white/30">
                    <Check size={10} strokeWidth={3} className="text-black" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Expanded Media Strip */}
        <AnimatePresence>
          {isStackExpanded && capturedMedia.length > 0 && (
            <motion.div
              key="expanded-strip"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 left-0 z-40 px-5"
              style={{ bottom: '130px' }}
            >
              {/* Backdrop to close */}
              <div
                className="fixed inset-0 z-[-1]"
                onClick={() => setIsStackExpanded(false)}
              />

              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="flex gap-2 overflow-x-auto no-scrollbar pt-3 pb-2 px-2 rounded-2xl bg-black/50 backdrop-blur-2xl border border-white/[0.1]"
              >
                {capturedMedia.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05, type: 'spring', stiffness: 400 }}
                    className="relative shrink-0 w-14 h-14 rounded-xl border border-white/20 bg-black/30"
                  >
                    <img
                      src={item.src}
                      alt=""
                      className="w-full h-full object-cover rounded-xl absolute inset-0 cursor-pointer"
                      onClick={() => setLightboxPhoto(item.src)}
                    />
                    {item.type === 'video' && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-5 h-5 rounded-full bg-black/60 flex items-center justify-center">
                          <div className="w-0 h-0 border-t-[3px] border-t-transparent border-l-[5px] border-l-white border-b-[3px] border-b-transparent ml-0.5" />
                        </div>
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        play('media.delete');
                        setCapturedMedia(prev => {
                          const next = prev.filter((_, i) => i !== index);
                          if (next.length <= 1) setIsStackExpanded(false);
                          return next;
                        });
                      }}
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black/70 backdrop-blur-md border border-white/20 flex items-center justify-center z-10 hover:bg-red-500/60 active:scale-90 transition-all"
                    >
                      <span className="text-white/90 text-[9px] font-bold leading-none">✕</span>
                    </button>
                  </motion.div>
                ))}

                {/* Collapse button */}
                <button
                  onClick={() => setIsStackExpanded(false)}
                  className="shrink-0 w-14 h-14 rounded-xl border border-white/10 bg-white/[0.05] flex items-center justify-center hover:bg-white/[0.1] active:scale-90 transition-all"
                >
                  <span className="text-white/50 text-[10px] font-medium">Done</span>
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Recording Indicator */}
        {isRecording && (
          <div className={`absolute left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1 rounded-full backdrop-blur-md z-30 below-top-signal ${recordingSeconds >= 25 ? 'bg-red-900/60' : 'bg-black/50'}`} style={{ top: 'calc(env(safe-area-inset-top, 0.75rem) + 48px)' }}>
            <div className={`w-2 h-2 rounded-full bg-red-500 ${recordingSeconds >= 25 ? 'animate-[pulse_0.4s_ease-in-out_infinite]' : 'animate-pulse'}`} />
            <span className={`font-mono ${recordingSeconds >= 25 ? 'text-red-300' : 'text-white'}`} style={{ fontSize: 'var(--text-xs)' }}>
              {recordingSeconds >= 25
                ? `${30 - recordingSeconds}s`
                : `${String(Math.floor(recordingSeconds / 60)).padStart(2, '0')}:${String(recordingSeconds % 60).padStart(2, '0')}`
              }
            </span>
          </div>
        )}

        {/* Status text removed — card + top icon already show connection state */}


        {/* AI Matrix Scan Effect */}
        <MatrixScanOverlay active={isScanning} duration={2500} columns={24} />

        {/* Emoji Rain Overlay */}
        <AnimatePresence>
          {emojiRain && (
            <div className="absolute inset-0 z-[25] pointer-events-none overflow-hidden">
              {emojiRainParticles.map((p, i) => (
                <motion.div
                  key={`emoji-${emojiRain.emojis.join('')}-${i}`}
                  initial={{ y: -50, x: `${p.x}%`, opacity: 1, scale: p.scale }}
                  animate={{ y: '110%', rotate: p.rotate }}
                  transition={{ duration: p.duration, delay: p.delay, ease: 'easeIn' }}
                  className="absolute"
                  style={{ fontSize: `${p.fontSize}px`, willChange: 'transform' }}
                >
                  {emojiRain.emojis[p.emojiIndex]}
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>

        {/* UI Badge Overlay */}
        <AnimatePresence>
          {uiBadge && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: uiBadge.position === 'bottom-center' ? 10 : -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className={`absolute z-[25] pointer-events-none flex justify-center ${uiBadge.position === 'top-center' ? 'top-4 left-0 right-0' :
                uiBadge.position === 'bottom-center' ? 'bottom-4 left-0 right-0' :
                  'top-1/2 left-0 right-0 -translate-y-1/2'
                }`}
            >
              <div className={`px-4 py-1.5 rounded-full backdrop-blur-md border font-semibold tracking-wide shadow-lg ${uiBadge.color === 'purple' ? 'bg-white/[0.12] border-white/20 text-white/80' :
                uiBadge.color === 'green' ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200 shadow-emerald-500/10' :
                  uiBadge.color === 'cyan' ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-200 shadow-cyan-500/10' :
                    'bg-blue-500/20 border-blue-500/40 text-blue-200 shadow-blue-500/10'
                }`} style={{ fontSize: 'var(--text-sm)' }}>
                {uiBadge.text}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Capture Genie Animation */}
        <AnimatePresence>
          {showCaptureAnim && (
            <>
              <motion.div
                initial={{ opacity: 0.7 }}
                animate={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 bg-white z-50 pointer-events-none"
              />
              <motion.div
                initial={{
                  top: '45%', left: '30%', width: '40%', height: '30%',
                  borderRadius: '8px', opacity: 1,
                }}
                animate={{
                  top: ['45%', '65%', '78%'],
                  left: ['30%', '10%', '4%'],
                  width: ['40%', '18%', '10%'],
                  height: ['30%', '14%', '8%'],
                  borderRadius: ['8px', '6px', '4px'],
                  opacity: [1, 0.85, 0],
                }}
                transition={{
                  duration: 0.55,
                  ease: [0.4, 0, 0.2, 1],
                  times: [0, 0.6, 1],
                }}
                className="absolute z-40 overflow-hidden shadow-2xl border border-white/30 pointer-events-none bg-neutral-800"
              />
            </>
          )}
        </AnimatePresence>

        {/* Action Card Overlay — removed (was LiveKit-driven) */}
      </div>

      {/* Top Controls + AI Card — vertical flex, card always 12px below buttons */}
      <div className="safe-area-top absolute top-0 left-0 right-0 px-4 z-20 pointer-events-none flex flex-col">
        {/* Row 1: Back button + Connection Signal + Flip Camera */}
        <div className="flex justify-between items-center pb-2">
          <button
            onClick={() => { play('nav.history'); onOpenHistory(); }}
            className="w-10 h-10 rounded-full flex items-center justify-center border border-white/[0.10] backdrop-blur-xl active:scale-90 transition-all pointer-events-auto"
            style={{ background: 'rgba(255,255,255,0.07)' }}
          >
            <ArrowLeft size={18} strokeWidth={2} className="text-white/80" />
          </button>

          {/* AI Connection Signal — centered */}
          <div className="pointer-events-auto">
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-xl border border-white/[0.08]"
              style={{ background: 'rgba(0,0,0,0.35)' }}
            >
              <AnimatePresence mode="wait">
                {connectionIcon === 'connecting' && (
                  <motion.div key="connecting" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-1.5">
                    <Loader2 size={11} className="text-blue-400 animate-spin" />
                    <span className="font-mono text-blue-400/80 tracking-[0.12em] uppercase" style={{ fontSize: '9px' }}>Connecting</span>
                  </motion.div>
                )}
                {connectionIcon === 'connected' && (
                  <motion.div key="connected" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    <span className="font-mono text-green-400/80 tracking-[0.12em] uppercase" style={{ fontSize: '9px' }}>Live</span>
                  </motion.div>
                )}
                {connectionIcon === 'weak' && (
                  <motion.div key="weak" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
                    <span className="font-mono text-yellow-400/80 tracking-[0.12em] uppercase" style={{ fontSize: '9px' }}>Weak</span>
                  </motion.div>
                )}
                {connectionIcon === 'offline' && (
                  <motion.div key="offline" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400/60" />
                    <span className="font-mono text-red-400/60 tracking-[0.12em] uppercase" style={{ fontSize: '9px' }}>Offline</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Flip Camera placeholder — camera switching not available without LiveKit */}
          <div className="flex items-center gap-1.5 pointer-events-auto">
            <div className="w-10 h-10" />
          </div>
        </div>

        {/* Row 2: AI Card — 12px below buttons */}
        <div className="pointer-events-auto" style={{ marginTop: '12px' }}>
          <Card
            isVisible={showCard}
            text={cardText}
            isMicOn={false}
            agentStatus={null}
            hasAgent={false}
            computedStatus={agentComputedStatus}
          />
        </div>
      </div>

      {/* Bottom Controls — [Album] [Dynamic Shutter] [Mic] */}
      <div className="absolute bottom-0 left-0 right-0 z-30">
        <div className="w-full flex items-center justify-center gap-8 px-6 py-4">
          {/* Album Upload — native <label> wrapping avoids iOS action sheet double-tap.
              Direct label→input association is the most native file picker pattern. */}
          <label
            className="w-14 h-14 rounded-full flex items-center justify-center border transition-all backdrop-blur-xl cursor-pointer active:scale-[0.85]"
            style={{
              background: 'rgba(255,255,255,0.06)',
              borderColor: 'rgba(255,255,255,0.08)',
            }}
          >
            <ImagePlus size={20} strokeWidth={1.8} className="text-white/60" />
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.heic,.heif,.gif,.webp,.mp4,.mov"
              multiple
              onChange={handleAlbumUpload}
              className="hidden"
            />
          </label>

          {/* Dynamic Shutter/Done — center */}
          {(() => {
            const hasMedia = capturedMedia.length > 0;
            const agentAction = null;
            const agentHasAction = false;
            const glowAction = (agentAction && agentAction !== 'dispatch' && agentAction !== 'ready' && agentAction !== 'done') ? agentAction : null;
            const glow = ACTION_GLOW[glowAction] || null;
            const glowShadow = glow ? glow.shadow : undefined;

            return (
              <div className="relative flex flex-col items-center">
                <button
                  onClick={handleShutterClick}
                  onPointerDown={handleShutterDown}
                  onPointerUp={handleShutterUp}
                  onPointerLeave={() => { if (!isRecording) clearTimeout(longPressTimerRef.current); }}
                  disabled={connectionIcon === 'offline' && !localCamera.isReady}
                  className="w-[76px] h-[76px] rounded-full flex items-center justify-center transition-colors duration-200 active:scale-95 select-none touch-none disabled:opacity-30"
                  style={{
                    border: isRecording
                      ? '4px solid rgba(239,68,68,0.6)'
                      : hasMedia
                        ? '4px solid rgba(34,197,94,0.7)'
                        : '4px solid rgba(255,255,255,0.7)',
                    background: 'transparent',
                    boxShadow: isRecording
                      ? 'none'
                      : hasMedia
                        ? '0 0 24px rgba(34,197,94,0.35)'
                        : (glowShadow || '0 0 20px rgba(255,255,255,0.1)'),
                  }}
                >
                  <AnimatePresence mode="wait">
                    {isRecording ? (
                      <motion.div
                        key="recording"
                        initial={{ scale: 0.5 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.5 }}
                        className="w-7 h-7 rounded-md bg-red-500 animate-pulse shadow-[0_0_20px_rgba(255,255,255,0.3)]"
                      />
                    ) : hasMedia ? (
                      /* Done state: green checkmark */
                      <motion.div
                        key="done"
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.5, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        className="w-[60px] h-[60px] rounded-full flex items-center justify-center"
                        style={{
                          background: 'linear-gradient(135deg, rgba(34,197,94,0.9), rgba(16,185,129,0.9))',
                          boxShadow: '0 0 20px rgba(34,197,94,0.4), inset 0 -2px 4px rgba(0,0,0,0.1), inset 0 2px 4px rgba(255,255,255,0.3)',
                        }}
                      >
                        <CheckCircle2 size={28} strokeWidth={2} className="text-white drop-shadow-sm" />
                      </motion.div>
                    ) : (
                      /* Idle state: scan icon or white circle */
                      <motion.div
                        key="idle"
                        initial={{ scale: 0.8 }}
                        animate={{
                          scale: agentHasAction ? [1, 1.08, 1] : 1,
                          opacity: 1,
                        }}
                        transition={agentHasAction ? { duration: 1.5, repeat: Infinity, ease: 'easeInOut' } : undefined}
                        className="w-[60px] h-[60px] rounded-full bg-white flex items-center justify-center"
                        style={{
                          boxShadow: agentHasAction
                            ? '0 0 25px rgba(34,211,238,0.4), inset 0 -2px 4px rgba(0,0,0,0.06), inset 0 2px 4px rgba(255,255,255,0.8)'
                            : '0 0 20px rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.06), inset 0 2px 4px rgba(255,255,255,0.8)',
                        }}
                      >
                        {agentHasAction ? (
                          <ScanLine size={24} strokeWidth={2} className="text-cyan-600" />
                        ) : null}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>
                {/* Label below shutter */}
                {!isRecording && (
                  <span
                    className="mt-1.5 text-center font-medium capitalize tracking-wide select-none"
                    style={{
                      fontSize: '10px',
                      color: hasMedia ? 'rgba(34,197,94,0.9)' : 'rgba(255,255,255,0.5)',
                      textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                    }}
                  >
                    {hasMedia ? 'Done' : 'Capture'}
                  </span>
                )}
              </div>
            );
          })()}

          {/* Mic placeholder — voice features not available without LiveKit */}
          <div className="w-14 h-14" />
        </div>
        <div className="w-full h-2 md:h-8 shrink-0" />
      </div>

      {/* Lightbox — fullscreen photo preview */}
      <AnimatePresence>
        {lightboxPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-xl"
            onClick={() => setLightboxPhoto(null)}
          >
            <motion.img
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              src={lightboxPhoto}
              alt="Full preview"
              className="max-w-[90%] max-h-[85%] object-contain rounded-2xl"
            />
            <button
              onClick={() => setLightboxPhoto(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center"
              style={{ top: 'calc(env(safe-area-inset-top, 0.75rem) + 8px)' }}
            >
              <X size={20} className="text-white/80" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
